import type React from 'react'
import { Suspense, useState } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Drawer from '@mui/material/Drawer'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Outlet } from 'react-router-dom'
import { ColorModeProvider } from '../../design/ColorModeProvider'
import { TrackSessionProvider } from '../../features/trackSession/TrackSessionContext'
import { FileParsingProvider } from '../providers/FileParsingContext'
import { VehicleDrawerProvider } from '../providers/VehicleDrawerContext'
import { LoadDrawerProvider } from '../providers/LoadDrawerContext'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'

const FULL_WIDTH = 240
const RAIL_WIDTH = 72
const COLLAPSE_KEY = 'pitbrain-v2-sidebar-collapsed'

function readUserCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

const BrandBar: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <Toolbar sx={{ px: collapsed ? 1 : 2 }}>
    <Typography variant="h6" noWrap>
      {collapsed ? 'PB' : 'Pitbrain'}
    </Typography>
  </Toolbar>
)

const AppShellLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userCollapsed, setUserCollapsed] = useState(readUserCollapsed)
  // Spec breakpoints: >=1280 full sidebar, 768-1279 icon rail, <768 drawer.
  const isWide = useMediaQuery('(min-width:1280px)')
  const isMedium = useMediaQuery('(min-width:768px)')
  const isMobile = !isMedium
  // Rail when the viewport forces it (768–1279) OR the user collapsed it.
  const collapsed = !isWide || userCollapsed
  const railWidth = collapsed ? RAIL_WIDTH : FULL_WIDTH

  const toggleUserCollapsed = (): void => {
    setUserCollapsed((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        // best-effort
      }
      return next
    })
  }

  const handleMenuClick = (): void => {
    if (isMobile) {
      setMobileOpen(true)
    } else {
      toggleUserCollapsed()
    }
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: railWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: railWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <BrandBar collapsed={collapsed} />
          <Sidebar collapsed={collapsed} />
        </Drawer>
      )}

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => {
            setMobileOpen(false)
          }}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: FULL_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {/* The mobile drawer is always full width, so show the full brand. */}
          <BrandBar collapsed={false} />
          <Sidebar
            onNavigate={() => {
              setMobileOpen(false)
            }}
          />
        </Drawer>
      )}

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <TopBar showMenuButton onMenuClick={handleMenuClick} />
        <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  py: 8,
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  )
}

/**
 * The /v2 shell. Owns the design-system theme (scoped here so v1 is unaffected)
 * and the page-data providers the wrapped real pages depend on — the same set
 * the legacy Dashboard supplies.
 */
const AppShell: React.FC = () => (
  <ColorModeProvider>
    <TrackSessionProvider>
      <FileParsingProvider>
        <VehicleDrawerProvider>
          <LoadDrawerProvider>
            <AppShellLayout />
          </LoadDrawerProvider>
        </VehicleDrawerProvider>
      </FileParsingProvider>
    </TrackSessionProvider>
  </ColorModeProvider>
)

export default AppShell
